import { useEffect, useRef, useState } from 'react'

interface Heading {
  id: string
  text: string
  level: number
}

interface Props {
  contentHtml: string
}

export default function TableOfContents({ contentHtml }: Props) {
  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const headingIdsRef = useRef<string[]>([])

  function collapseWs(text: string): string {
    return (text || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim()
  }

  function looksLikeHeadingText(text: string): boolean {
    const t = collapseWs(text)
    if (!t) return false
    if (t.length < 2 || t.length > 52) return false
    if (/[.?!:]$/.test(t)) return false
    if (/[;,]/.test(t)) return false
    if (t.includes('http://') || t.includes('https://')) return false

    const words = t.split(/\s+/).filter(Boolean)
    if (words.length > 6) return false

    // All-lowercase looks like a sentence fragment, not a heading.
    if (t.toLowerCase() === t) return false

    // Title-ish: allow small connector words, otherwise require non-lowercase starts.
    const small = new Set(['of', 'and', 'or', 'to', 'the', 'a', 'an', 'in', 'on', 'for', 'with', 'vs'])
    for (const w of words) {
      const clean = w.replace(/[^A-Za-z0-9]/g, '')
      if (!clean) continue
      const lower = clean.toLowerCase()
      if (small.has(lower)) continue
      const first = clean[0]
      if (first >= 'a' && first <= 'z') return false
    }

    return true
  }

  function promoteParagraphHeadings(container: Element) {
    // Some pages ended up with headings demoted into plain <p> lines due to editor/import quirks.
    // If we have *zero* headings, promote very conservative "title-ish" paragraphs into h2/h3.
    if (container.querySelector('h2, h3')) return

    const disallowedAncestor = 'li, blockquote, pre, table, thead, tbody, tr, th, td'
    const ps = Array.from(container.querySelectorAll('p'))
    for (const p of ps) {
      if (p.closest(disallowedAncestor)) continue

      const nonBrChildren = Array.from(p.children || []).filter(
        el => (el.tagName || '').toLowerCase() !== 'br',
      )
      if (nonBrChildren.length) continue

      const text = collapseWs(p.textContent || '')
      if (!looksLikeHeadingText(text)) continue

      const prev = p.previousElementSibling
      const tag = prev && (prev.tagName === 'H2' || prev.tagName === 'H3') ? 'h3' : 'h2'
      const h = document.createElement(tag)
      h.textContent = text
      p.replaceWith(h)
    }
  }

  function repairInvalidHeadings(container: Element) {
    // Some imported/pasted HTML ends up with invalid structures like:
    // <h2><p>...</p><ul>...</ul></h2>
    // Repair by keeping a real heading and moving block children outside.
    const invalidBlockTags = new Set([
      'p', 'ul', 'ol', 'li',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'pre', 'blockquote',
    ])

    const hs = Array.from(container.querySelectorAll('h2, h3'))
    for (const h of hs) {
      const blockChildren = Array.from(h.children).filter(el => invalidBlockTags.has(el.tagName.toLowerCase()))
      if (!blockChildren.length) continue

      // Label: prefer inline text before the first block child, else the first block child's text.
      const firstBlock = blockChildren[0]
      const parts: string[] = []
      for (const child of Array.from(h.childNodes)) {
        if (child === firstBlock) break
        if (child.nodeType === Node.TEXT_NODE) {
          parts.push(child.textContent || '')
          continue
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child as Element
          if (invalidBlockTags.has(el.tagName.toLowerCase())) break
          parts.push(el.textContent || '')
        }
      }

      let label = collapseWs(parts.join(' '))
      let dropFirstBlock = false
      if (!label) {
        label = collapseWs(firstBlock.textContent || '')
        dropFirstBlock = true
      }
      // If we can't derive a safe label, don't mutate the heading. (Avoids "Section" placeholders.)
      if (!label) continue

      const parent = h.parentNode
      if (!parent) continue

      // Move all block children (except the first, which becomes the label) after the heading.
      let cursor: Node = h
      const startIdx = dropFirstBlock ? 1 : 0
      for (let i = startIdx; i < blockChildren.length; i += 1) {
        const el = blockChildren[i]
        parent.insertBefore(el, cursor.nextSibling)
        cursor = el
      }

      // Setting textContent clears any remaining (invalid) children, including the first block child.
      h.textContent = label
    }
  }

  function slugify(text: string): string {
    return text
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  function assignHeadingIds(container: Element): Heading[] {
    const elements = container.querySelectorAll('h2, h3')
    const used = new Map<string, number>()
    const items: Heading[] = []

    elements.forEach((el, idx) => {
      const text = (el.textContent || '').trim()
      if (!text) return
      let base = slugify(text)
      if (!base) base = `heading-${idx}`

      const count = used.get(base) || 0
      const nextCount = count + 1
      used.set(base, nextCount)
      const id = nextCount === 1 ? base : `${base}-${nextCount}`

      el.id = id
      items.push({
        id,
        text,
        level: el.tagName === 'H2' ? 2 : 3,
      })
    })

    return items
  }

  function scrollToHeading(id: string, behavior: ScrollBehavior = 'smooth') {
    const el = document.getElementById(id)
    if (!el) return
    const offset = 96
    const y = window.scrollY + el.getBoundingClientRect().top - offset
    window.scrollTo({ top: y, behavior })
  }

  useEffect(() => {
    // Parse headings from the rendered content
    const timer = setTimeout(() => {
      const container = document.querySelector('.wiki-content')
      if (!container) return

      promoteParagraphHeadings(container)
      repairInvalidHeadings(container)
      const items = assignHeadingIds(container)
      setHeadings(items)
      headingIdsRef.current = items.map(i => i.id)

      // Heading ids don't exist at document load, so the browser can't honor a
      // #hash deep link (e.g. the Patch Notes hub's section chips) on its own.
      try {
        const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''))
        if (hash && items.some(i => i.id === hash)) {
          setActiveId(hash)
          scrollToHeading(hash, 'auto')
        }
      } catch { /* malformed hash — ignore */ }
    }, 100)

    return () => clearTimeout(timer)
  }, [contentHtml])

  // Scroll spy (deterministic): last heading above the header offset wins.
  useEffect(() => {
    if (headings.length < 1) return

    let raf = 0
    const offset = 96

    function updateActive() {
      raf = 0
      const ids = headingIdsRef.current
      if (!ids.length) return

      let nextActive = ids[0]
      for (const id of ids) {
        const el = document.getElementById(id)
        if (!el) continue
        const top = el.getBoundingClientRect().top
        if (top <= offset + 1) {
          nextActive = id
          continue
        }
        break
      }

      setActiveId(prev => (prev === nextActive ? prev : nextActive))
    }

    function onScrollOrResize() {
      if (raf) return
      raf = window.requestAnimationFrame(updateActive)
    }

    window.addEventListener('scroll', onScrollOrResize, { passive: true })
    window.addEventListener('resize', onScrollOrResize)
    onScrollOrResize()

    return () => {
      window.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [headings])

  if (headings.length < 2) return null

  return (
    <aside className="hidden min-[1280px]:block w-52 shrink-0 sticky top-[var(--wiki-sticky-top)] self-start max-h-[var(--wiki-sticky-max-h)] overflow-y-auto pl-4 border-l border-calamity-border-subtle/25">
      <h4 className="text-[10px] uppercase tracking-[0.15em] text-calamity-text-muted font-bold mb-3 px-1">
        On this page
      </h4>

      <nav className="space-y-0.5">
        {headings.map(h => (
          <a
            key={h.id}
            href={`#${h.id}`}
            onClick={e => {
              e.preventDefault()
              setActiveId(h.id)
              scrollToHeading(h.id, 'smooth')
              try { history.replaceState(null, '', `#${h.id}`) } catch {}
            }}
            className={`
              block text-xs py-1.5 transition-colors border-l-2 rounded-md
              ${h.level === 3 ? 'pl-5 pr-2' : 'pl-3 pr-2'}
              ${activeId === h.id
                ? 'border-calamity-accent text-calamity-text bg-calamity-accent/10'
                : 'border-transparent text-calamity-text-muted hover:text-calamity-text-dim hover:bg-calamity-panel-hover/25'
              }
            `}
          >
            {(() => {
              const t = (h.text || '').replace(/\s+/g, ' ').trim()
              if (t.length <= 90) return t
              return `${t.slice(0, 87)}...`
            })()}
          </a>
        ))}
      </nav>
    </aside>
  )
}
